import 'devextreme/dist/css/dx.material.blue.light.css';
import { DataGrid, DataGridTypes, Export, FilterRow, Grouping, GroupItem, GroupPanel, Pager, Paging, Selection, Summary, TotalItem } from 'devextreme-react/data-grid';
import { DataGridProps } from './types';
import { defaultDateFormat, defaultExportFormats, defaultPageIndex, defaultPageSize, pageSizes } from './constants';
import { useCallback, useState } from 'react';
import { Row } from 'devextreme/ui/data_grid_types';
import Paper from '@mui/material/Paper/Paper';
import styled from '@emotion/styled';
import ReportLoader from 'components/ReportsComponents/ReportLoader';
import { ReportColumn } from './ReportColumn';
import { handleExport } from './utils/handleExport';


export default function DevExtremeReportGrid({
    columns,
    displayGroupingPanel = false,
    handleNewSheet,
    headerTitleForSheet,
    isLoading,
    pdfOrientation,
    reportHeaderTagLine,
    rows,
    title,
    cellPrepared,
    customizeCell,
    exportFormats = defaultExportFormats,
    showSelection = true,
    showPaging = true,
    allowSorting = true,
    showFiltering = true,
    allowGrouping = true,
    emptyPanelText,
    dateFormat = defaultDateFormat,
    summaryItems,
    calculateSelectedRow,
    groupCellContent: GroupCellContent,
    groupSummaryItems,
}: Readonly<DataGridProps>) {

    const [selectedRows, setSelectedRows] = useState<Row[]>([]);

    const onSelectionChanged = useCallback(
        (e: DataGridTypes.SelectionChangedEvent) => {
            setSelectedRows(e.selectedRowsData);
        }, [],
    );

    const onExporting = useCallback(
        (e: DataGridTypes.ExportingEvent) => handleExport(title, headerTitleForSheet, reportHeaderTagLine, customizeCell, handleNewSheet, pdfOrientation)(e),
        [title, headerTitleForSheet, reportHeaderTagLine, customizeCell, handleNewSheet, pdfOrientation],
    );

    return (
        <>
            {isLoading ? (
                <ReportLoader />
            ) : (
                rows &&
                rows?.length > 0 && (
                    <CustomPaper elevation={3}>
                        <DataGrid
                            dataSource={rows}
                            showBorders
                            onExporting={onExporting}
                            onSelectionChanged={onSelectionChanged}
                            onCellPrepared={cellPrepared}
                            columnAutoWidth
                            columnMinWidth={50}
                            allowColumnResizing
                            columnResizingMode="widget"
                        >
                            {showSelection && (
                                <Selection mode="multiple" selectAllMode="allPages" />
                            )}

                            <Paging
                                defaultPageIndex={defaultPageIndex}
                                defaultPageSize={defaultPageSize}
                            />
                            <Pager
                                allowedPageSizes={pageSizes}
                                visible={showPaging}
                                displayMode="full"
                                showPageSizeSelector
                                showNavigationButtons
                                showInfo
                            />

                            <FilterRow visible={showFiltering} />
                            <GroupPanel
                                visible={displayGroupingPanel}
                                emptyPanelText={emptyPanelText || "Drag and drop the filter."}

                            />
                            <Grouping autoExpandAll={false} contextMenuEnabled />
                            {columns.map((column) => (
                                <ReportColumn
                                    allowGrouping={allowGrouping}
                                    allowSorting={allowSorting}
                                    dateFormat={dateFormat}
                                    column={column}
                                    key={column.title}
                                    groupCellContent={GroupCellContent}>

                                    {column.children?.map((childColumn: any) => (
                                        <ReportColumn
                                            column={childColumn}
                                            key={childColumn.title}
                                        ></ReportColumn>
                                    ))}
                                </ReportColumn>
                            ))}

                            {((summaryItems && summaryItems?.length > 0) || (groupSummaryItems && groupSummaryItems?.length > 0)) && (
                                <Summary calculateCustomSummary={(options) => calculateSelectedRow?.(options, rows)}>
                                    {groupSummaryItems?.map((item) => (
                                        <GroupItem
                                            key={item.name ?? item.columnName ?? item.showInColumn}
                                            column={item.columnName}
                                            showInColumn={item.showInColumn}
                                            customizeText={({ value }) =>
                                                item.customizeText ? String(item.customizeText({ value })) : String(value)
                                            }
                                            name={item.name ?? item.columnName}
                                            summaryType={item.type}
                                            showInGroupFooter={item.showInGroupFooter}
                                            alignByColumn={item.alignByColumn}
                                            skipEmptyValues={false}
                                        />
                                    ))}
                                    {summaryItems?.map((item) => (
                                        <TotalItem
                                            key={item.name ?? item.columnName ?? item.showInColumn}
                                            column={item.columnName}
                                            name={item.name ?? item.columnName}
                                            showInColumn={item.showInColumn}
                                            summaryType={item.type}
                                            customizeText={({ value }) =>
                                                item.customizeText ? String(item.customizeText({ value })) : String(value)
                                            }
                                        />
                                    ))}
                                </Summary>
                            )}

                            <Export
                                enabled
                                allowExportSelectedData={selectedRows.length > 0}
                                formats={exportFormats}
                            />
                        </DataGrid>
                    </CustomPaper>
                )
            )}
        </>
    );
}

const CustomPaper = styled(Paper)`
   margin-bottom: 20px;
   overflow-x: auto;
   max-width: 100%;

  .dx-datagrid-summary-item,
  .dx-datagrid-group-footer .dx-datagrid-text-content {
    white-space: pre-line;
    line-height: 1.4;
  }

  .dx-scrollable-scroll-content {
    background-color: ${({ theme }) => theme.palette.primary.main} !important;
  }
  .dx-datagrid .dx-selection {
    background-color: ${({ theme }) => theme.palette.primary.main} !important;
  }
  .dx-checkbox-checked .dx-checkbox-icon {
    background-color: ${({ theme }) => theme.palette.primary.main} !important;
  }
  .dx-checkbox-indeterminate .dx-checkbox-icon {
    background-color: ${({ theme }) => theme.palette.primary.main} !important;
    color: ${({ theme }) => theme.palette.primary.main} !important;
  }
  .dx-datagrid-header-panel { 
    background-color: ${({ theme }) => theme.palette.reports.header} !important; 
  }
  .dx-datagrid-text-content {
    color: ${({ theme }) => theme.palette.primary.dark} !important;
    font-weight: 600;
  }
  .pink {
    background-color: ${({ theme }) => theme.palette.reports.pink} !important;
  }
  .green {
    background-color: ${({ theme }) => theme.palette.reports.green} !important;
  } 
`;